import { META_KEYS, metaSet } from '../db/db'

/**
 * §10.4. Call `navigator.storage.persist()` on first run and **log the result**.
 *
 * Expect `true` on Chromium under some conditions, and expect it to be
 * unavailable or `false` on Safari — `persist()` is not
 * meaningfully implemented there, which is why the install nudge (§10.3) is a
 * data-durability feature rather than a growth tactic. Never assume success.
 */
export interface PersistenceReport {
  supported: boolean
  granted: boolean | null
  quotaBytes: number | null
  usageBytes: number | null
}

export async function requestPersistence(): Promise<PersistenceReport> {
  const supported = typeof navigator !== 'undefined' && 'storage' in navigator && 'persist' in navigator.storage

  let granted: boolean | null = null
  if (supported) {
    try {
      granted = (await navigator.storage.persisted()) || (await navigator.storage.persist())
    } catch (error) {
      console.warn('[storage] persist() threw', error)
      granted = false
    }
  }

  const estimate = await estimateStorage()
  console.info('[storage] persist()', { supported, granted, ...estimate })
  await metaSet(META_KEYS.persistGranted, { supported, granted, at: Date.now() })

  return { supported, granted, ...estimate }
}

export async function estimateStorage(): Promise<{ quotaBytes: number | null; usageBytes: number | null }> {
  if (typeof navigator === 'undefined' || !('storage' in navigator) || !('estimate' in navigator.storage)) {
    return { quotaBytes: null, usageBytes: null }
  }
  try {
    const { quota, usage } = await navigator.storage.estimate()
    return { quotaBytes: quota ?? null, usageBytes: usage ?? null }
  } catch {
    return { quotaBytes: null, usageBytes: null }
  }
}

/** §10.3: on iOS only an installed web app keeps its storage through days of idleness. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  return window.matchMedia('(display-mode: standalone)').matches || iosStandalone
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—'
  if (bytes < 1024) return `${bytes} B`
  const units = ['kB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(value < 10 ? 2 : 1)} ${units[unit]}`
}
